import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';
import type {
  LotAllocation,
  LotDetail,
  LotListItem,
  LotPagedResult,
  LotReconcileRow,
  LotSettings,
  LotTransaction,
} from '../types/lots.types';

const dataOf = (body: any) => body?.data ?? body?.Data ?? body;

const num = (value: any) => Number(value ?? 0);

const mapLot = (raw: any): LotListItem => ({
  id: raw.id ?? raw.Id,
  itemId: raw.itemId ?? raw.ItemId,
  itemName: raw.itemName ?? raw.ItemName,
  stockCode: raw.stockCode ?? raw.StockCode,
  lotNo: raw.lotNo ?? raw.LotNo ?? '',
  supplierLotNo: raw.supplierLotNo ?? raw.SupplierLotNo,
  productionDate: raw.productionDate ?? raw.ProductionDate ?? null,
  expiryDate: raw.expiryDate ?? raw.ExpiryDate ?? null,
  daysLeft: raw.daysLeft ?? raw.DaysLeft ?? null,
  status: raw.status ?? raw.Status ?? '',
  holdReason: raw.holdReason ?? raw.HoldReason ?? null,
  sourceType: raw.sourceType ?? raw.SourceType ?? '',
  notes: raw.notes ?? raw.Notes ?? null,
  quantity: num(raw.quantity ?? raw.Quantity),
  balances: (raw.balances ?? raw.Balances ?? []).map((b: any) => ({
    warehouseId: b.warehouseId ?? b.WarehouseId,
    quantity: num(b.quantity ?? b.Quantity),
  })),
});

const mapTxn = (raw: any): LotTransaction => ({
  id: raw.id ?? raw.Id,
  lotId: raw.lotId ?? raw.LotId ?? null,
  direction: raw.direction ?? raw.Direction ?? '',
  quantity: num(raw.quantity ?? raw.Quantity),
  kind: raw.kind ?? raw.Kind ?? '',
  movementType: raw.movementType ?? raw.MovementType ?? null,
  referenceNumber: raw.referenceNumber ?? raw.ReferenceNumber ?? null,
  warehouseId: raw.warehouseId ?? raw.WarehouseId,
  createdAt: raw.createdAt ?? raw.CreatedAt,
  isReversed: Boolean(raw.isReversed ?? raw.IsReversed),
  isExpiredAtUse: Boolean(raw.isExpiredAtUse ?? raw.IsExpiredAtUse),
});

export const lotService = {
  async paged(request: {
    search?: string;
    itemId?: string;
    warehouseId?: string;
    status?: string;
    expiryFilter?: string;
    onlyWithBalance?: boolean;
    page: number;
    pageSize: number;
  }): Promise<LotPagedResult> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOTS_PAGED, {
      Search: request.search || null,
      ItemId: request.itemId || null,
      WarehouseId: request.warehouseId || null,
      Status: request.status || null,
      ExpiryFilter: request.expiryFilter || 'ALL',
      OnlyWithBalance: request.onlyWithBalance ?? true,
      Page: request.page,
      PageSize: request.pageSize,
    });
    const data = dataOf(response.data);
    const items = (data.items ?? data.Items ?? []).map(mapLot);
    return {
      items,
      totalCount: data.totalCount ?? data.TotalCount ?? 0,
      page: data.page ?? data.Page ?? request.page,
      pageSize: data.pageSize ?? data.PageSize ?? request.pageSize,
      totalPages: data.totalPages ?? data.TotalPages ?? 0,
    };
  },

  async byItem(itemId: string): Promise<LotListItem[]> {
    const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_LOTS_BY_ITEM(itemId));
    return (dataOf(response.data) ?? []).map(mapLot);
  },

  async detail(id: string): Promise<LotDetail> {
    const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_LOT(id));
    const data = dataOf(response.data);
    return {
      lot: mapLot(data.lot ?? data.Lot),
      transactions: (data.transactions ?? data.Transactions ?? []).map(mapTxn),
    };
  },

  async propose(itemId: string, warehouseId: string, quantity: number): Promise<LotAllocation> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_PROPOSE, {
      ItemId: itemId,
      WarehouseId: warehouseId,
      Quantity: quantity,
    });
    const data = dataOf(response.data);
    return {
      picks: (data.picks ?? data.Picks ?? []).map((p: any) => ({
        lotId: p.lotId ?? p.LotId ?? null,
        quantity: num(p.quantity ?? p.Quantity),
        expired: Boolean(p.expired ?? p.Expired),
      })),
      unassigned: num(data.unassigned ?? data.Unassigned),
      warnings: data.warnings ?? data.Warnings ?? [],
    };
  },

  async update(id: string, body: {
    supplierLotNo?: string;
    productionDate?: string | null;
    expiryDate?: string | null;
    notes?: string;
  }): Promise<void> {
    await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_UPDATE(id), {
      SupplierLotNo: body.supplierLotNo ?? null,
      ProductionDate: body.productionDate || null,
      ExpiryDate: body.expiryDate || null,
      Notes: body.notes ?? null,
    });
  },

  async hold(id: string, hold: boolean, reason?: string): Promise<void> {
    await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_HOLD(id), {
      Hold: hold,
      Reason: reason ?? null,
    });
  },

  async getSettings(): Promise<LotSettings> {
    const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_LOT_SETTINGS);
    const data = dataOf(response.data);
    return {
      lotNoPattern: data.lotNoPattern ?? data.LotNoPattern ?? '{YYMMDD}-{SEQ}',
      nearExpiryDays: data.nearExpiryDays ?? data.NearExpiryDays ?? 30,
      blockExpiredInProduction: Boolean(data.blockExpiredInProduction ?? data.BlockExpiredInProduction),
      blockExpiredInSales: Boolean(data.blockExpiredInSales ?? data.BlockExpiredInSales),
    };
  },

  async saveSettings(settings: LotSettings): Promise<void> {
    await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_SETTINGS_SAVE, {
      LotNoPattern: settings.lotNoPattern,
      NearExpiryDays: settings.nearExpiryDays,
      BlockExpiredInProduction: settings.blockExpiredInProduction,
      BlockExpiredInSales: settings.blockExpiredInSales,
    });
  },

  async reconcilePreview(itemIds?: string[]): Promise<LotReconcileRow[]> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_RECONCILE_PREVIEW, {
      ItemIds: itemIds ?? null,
    });
    return (dataOf(response.data) ?? []).map((raw: any) => ({
      itemId: raw.itemId ?? raw.ItemId,
      itemName: raw.itemName ?? raw.ItemName ?? '',
      stockCode: raw.stockCode ?? raw.StockCode ?? null,
      warehouseId: raw.warehouseId ?? raw.WarehouseId,
      warehouseName: raw.warehouseName ?? raw.WarehouseName ?? null,
      stockQuantity: num(raw.stockQuantity ?? raw.StockQuantity),
      targetQuantity: num(raw.targetQuantity ?? raw.TargetQuantity),
      lotQuantity: num(raw.lotQuantity ?? raw.LotQuantity),
      difference: num(raw.difference ?? raw.Difference),
    }));
  },

  async reconcile(itemIds?: string[]): Promise<number> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_RECONCILE, {
      ItemIds: itemIds ?? null,
    });
    const data = dataOf(response.data);
    return num(data.count ?? data.Count);
  },

  async trace(lotId: string, direction: 'BACKWARD' | 'FORWARD'): Promise<any> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_TRACE, {
      LotId: lotId,
      Direction: direction,
    });
    const body = response.data ?? {};
    return {
      root: body.data ?? body.Data ?? null,
      truncated: Boolean(body.truncated ?? body.Truncated),
      warning: body.warning ?? body.Warning ?? null,
    };
  },

  async recall(lotId: string): Promise<any[]> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_RECALL, { LotId: lotId });
    const data = dataOf(response.data);
    return data?.rows ?? data?.Rows ?? [];
  },

  async findByInvoice(invoiceId: string): Promise<any[]> {
    const response = await accountingApi.post(ACCOUNTING_API.INVENTORY_LOT_FIND_BY_INVOICE, {
      InvoiceId: invoiceId,
    });
    return dataOf(response.data) ?? [];
  },
};
